import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_ENDPOINTS } from '../../config/api';
import { logoutUser } from './authSlice';

export const mapCouponTypeToDisplay = (couponType) => {
  return {
    id: couponType.ctId || couponType.id,
    ctCouponType: couponType.ctCouponType,
    ctName: couponType.ctName,
    ctAmountValue: couponType.ctAmountValue,
    ctNumUse: couponType.ctNumUse,
    ctExpiryDate: couponType.ctExpiryDate,
    ctFlagShip: couponType.ctFlagShip,
    typeCode: couponType.ctCouponType === 'charge' ? String(couponType.ctAmountValue) : 'freeTrial',
    name: couponType.ctName,
    amount: couponType.ctCouponType === 'charge' ? couponType.ctAmountValue : 0,
    validityType: couponType.ctCouponType === 'freeTrial' ? 'days' : 'times',
    validityValue: couponType.ctCouponType === 'freeTrial' ? couponType.ctAmountValue : couponType.ctNumUse,
    sellable: couponType.ctFlagShip,
    status: '有効',
  };
};

export const fetchCouponTypes = createAsyncThunk(
  'couponType/fetchCouponTypes',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth?.token;
      const accountType = auth?.user?.cmAccountType;

      const isClient = accountType === 'client';
      const endpoint = isClient
        ? API_ENDPOINTS.CLIENT_COUPON_TYPES
        : API_ENDPOINTS.ADMIN_COUPON_TYPES;

      const headers = {
        'Accept': 'application/json',
      };

      if (!isClient && !token) {
        return rejectWithValue('認証が必要です');
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(endpoint, {
        method: 'GET',
        headers,
      });
      const result = await response.json();
      if (!response.ok) {
        return rejectWithValue(result?.message || 'クーポン種類一覧の取得に失敗しました');
      }
      const rows = Array.isArray(result?.data) ? result.data : [];
      return rows.map(mapCouponTypeToDisplay);
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const createCouponType = createAsyncThunk(
  'couponType/createCouponType',
  async (typeData, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth?.token;

      if (!token) {
        return rejectWithValue('認証が必要です');
      }

      const cmId = auth?.user?.cmId || auth?.user?.id;
      if (!cmId) {
        return rejectWithValue('管理者情報が見つかりません');
      }

      const rawExpiry = typeData.ctExpiryDate;
      const parsedExpiry =
        rawExpiry === '' || rawExpiry === null || rawExpiry === undefined
          ? 0
          : parseInt(rawExpiry, 10) || 0;

      const payload = {
        ctCouponType: typeData.ctCouponType,
        ctName: typeData.ctName,
        ctAmountValue: parseFloat(typeData.ctAmountValue) || 0,
        ctNumUse: parseFloat(typeData.ctNumUse) || 1,
        ctExpiryDate: parsedExpiry,
        ctFlagShip: typeData.ctFlagShip !== undefined ? typeData.ctFlagShip : true,
        cmId: cmId,
      };

      const response = await fetch(API_ENDPOINTS.ADMIN_COUPON_TYPES, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data?.message || 'クーポン種類の作成に失敗しました');
      }

      const couponType = data?.couponType || data?.data || data;
      return {
        id: couponType.ctId || couponType.id,
        ctCouponType: couponType.ctCouponType,
        ctName: couponType.ctName,
        ctAmountValue: couponType.ctAmountValue,
        ctNumUse: couponType.ctNumUse,
        ctExpiryDate: couponType.ctExpiryDate,
        ctFlagShip: couponType.ctFlagShip,
        typeCode: couponType.ctCouponType === 'charge' ? String(couponType.ctAmountValue) : 'freeTrial',
        name: couponType.ctName,
        amount: couponType.ctCouponType === 'charge' ? couponType.ctAmountValue : 0,
        validityType: couponType.ctCouponType === 'freeTrial' ? 'days' : 'times',
        validityValue: couponType.ctCouponType === 'freeTrial' ? couponType.ctAmountValue : couponType.ctNumUse,
        sellable: couponType.ctFlagShip,
        status: '有効',
      };
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const updateCouponType = createAsyncThunk(
  'couponType/updateCouponType',
  async ({ id, data }, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth?.token;
      if (!token) {
        return rejectWithValue('認証が必要です');
      }
      const payload = {
        ctFlagShip: data.ctFlagShip !== undefined ? data.ctFlagShip : true,
      };
      const response = await fetch(API_ENDPOINTS.ADMIN_COUPON_TYPE_FLAGSHIP(id), {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok) {
        return rejectWithValue(result?.message || 'クーポン種類の更新に失敗しました');
      }
      const couponType = result?.couponType || result?.data || result;
      return mapCouponTypeToDisplay({ ...couponType, ctId: couponType.ctId || id });
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const deleteCouponType = createAsyncThunk(
  'couponType/deleteCouponType',
  async (id, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const token = auth?.token;
      const accountType = auth?.user?.cmAccountType;

      if (!token) {
        return rejectWithValue('認証が必要です');
      }

      if (accountType === 'client') {
        return rejectWithValue('クライアントはクーポン種類を削除できません');
      }

      const response = await fetch(API_ENDPOINTS.ADMIN_COUPON_TYPE(id), {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        return rejectWithValue(data?.message || 'クーポン種類の削除に失敗しました');
      }

      return id;
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const toggleSellable = createAsyncThunk(
  'couponType/toggleSellable',
  async ({ id, sellable }, { rejectWithValue }) => {
    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      return { id, sellable };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  couponTypes: [],
  currentPage: 1,
  isLoading: false,
  error: null,
  isCreateModalOpen: false,
  isEditModalOpen: false,
  editingType: null,
};

const couponTypeSlice = createSlice({
  name: 'couponType',
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    openCreateModal: (state) => {
      state.isCreateModalOpen = true;
    },
    closeCreateModal: (state) => {
      state.isCreateModalOpen = false;
    },
    openEditModal: (state, action) => {
      state.isEditModalOpen = true;
      state.editingType = action.payload;
    },
    closeEditModal: (state) => {
      state.isEditModalOpen = false;
      state.editingType = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    upsertCouponTypeFromSocket: (state, action) => {
      const mapped = mapCouponTypeToDisplay(action.payload || {});
      if (mapped?.id == null) return;
      const idx = state.couponTypes.findIndex((t) => t.id === mapped.id);
      if (idx === -1) {
        state.couponTypes.unshift(mapped);
        state.currentPage = 1;
      } else {
        state.couponTypes[idx] = { ...state.couponTypes[idx], ...mapped };
      }
    },
    removeCouponTypeFromSocket: (state, action) => {
      const id = action.payload?.id ?? action.payload?.ctId;
      if (id == null) return;
      state.couponTypes = state.couponTypes.filter((t) => t.id !== id);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCouponTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCouponTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.couponTypes = action.payload;
      })
      .addCase(fetchCouponTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createCouponType.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createCouponType.fulfilled, (state, action) => {
        state.isLoading = false;
        state.couponTypes.push(action.payload);
        state.isCreateModalOpen = false;
      })
      .addCase(createCouponType.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateCouponType.fulfilled, (state, action) => {
        const index = state.couponTypes.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.couponTypes[index] = { ...state.couponTypes[index], ...action.payload };
        }
        state.isEditModalOpen = false;
        state.editingType = null;
      })
      .addCase(deleteCouponType.fulfilled, (state, action) => {
        state.couponTypes = state.couponTypes.filter(t => t.id !== action.payload);
      })
      .addCase(toggleSellable.fulfilled, (state, action) => {
        const index = state.couponTypes.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.couponTypes[index].sellable = action.payload.sellable;
        }
      })
      .addCase(logoutUser.fulfilled, () => ({ ...initialState }));
  },
});

export const {
  setCurrentPage,
  openCreateModal,
  closeCreateModal,
  openEditModal,
  closeEditModal,
  clearError,
  upsertCouponTypeFromSocket,
  removeCouponTypeFromSocket,
} = couponTypeSlice.actions;

export default couponTypeSlice.reducer;
